import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useBrand } from "@/config/brands";
import { FileText } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from 'react-i18next';

const TermsOfService = () => {
  const brand = useBrand();
  const { t } = useTranslation('legal');

  return (
    <div className="min-h-screen bg-background pb-24">
      <Helmet>
        <title>{t('terms.meta.pageTitle', { brandName: brand.name })}</title>
        <meta name="description" content={t('terms.meta.description', { brandName: brand.name })} />
        <link rel="canonical" href={`${brand.primaryDomain}/terms`} />
        <meta property="og:title" content={t('terms.meta.pageTitle', { brandName: brand.name })} />
        <meta property="og:description" content={t('terms.meta.description', { brandName: brand.name })} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/terms`} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content={brand.social.twitter} />
        <meta name="twitter:title" content={t('terms.meta.pageTitle', { brandName: brand.name })} />
        <meta name="twitter:description" content={t('terms.meta.description', { brandName: brand.name })} />
      </Helmet>

      {/* Header */}
      <div className="bg-gradient-to-br from-matrix-green/10 to-blood-red/10 border-b border-matrix-green/20">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-8 h-8 text-matrix-green" />
            <h1 className="text-3xl font-bold text-foreground">{t('terms.title')}</h1>
          </div>
          <p className="text-muted-foreground">{t('common.lastUpdated', { date: 'January 2025' })}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.acceptance')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              By accessing and using {brand.name} {brand.legal.organization && brand.legal.organization !== brand.name && `("${brand.legal.organization}")`}, you accept and agree to be bound by the terms and provision of this agreement.
              If you do not agree to abide by the above, please do not use this service.
            </p>
            <p className="font-semibold text-foreground">
              Age Requirement
            </p>
            <p>
              You must be at least 18 years of age to access or use {brand.name}. By using this service, you represent
              and warrant that you are at least 18 years old. If you are under 18, you may not use this service
              under any circumstances.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.useLicense')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              Permission is granted to access the hospital pricing materials on {brand.name} for any lawful purpose,
              including journalism, research, advocacy, patient education, and commercial use.
              The underlying chargemaster data we surface is published by hospitals under federal CMS Hospital
              Price Transparency requirements (45 CFR Part 180) and is in the public interest. Under this license you may not:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Misrepresent {brand.name} as the original source of the underlying pricing data (it comes from each hospital&apos;s federally-mandated chargemaster file)</li>
              <li>Attempt to decompile or reverse engineer the {brand.name} software or scrape the site at a rate that disrupts service</li>
              <li>Remove copyright or attribution notations from materials we author (e.g., editorial commentary, methodology documentation)</li>
              <li>Use the data to construct misleading claims about specific hospitals (e.g., editing line-item context out)</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.dataAndInformation')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              All hospital pricing data displayed on {brand.name} is sourced from publicly available CMS-mandated
              price transparency files published by hospitals. While we strive for accuracy, we make no warranties
              about the completeness, reliability, or accuracy of this information.
            </p>
            <p>
              The pricing data shown is the actual gross charge (sticker price) each hospital is federally required
              to publish, and is the price uninsured patients are commonly billed. Patients with insurance may pay
              negotiated rates instead, but the chargemaster price is what appears on the initial bill, what
              determines collections accounts, and what hospitals report to CMS.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.medicalCodingDisclaimers')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p className="font-semibold text-foreground">
              CPT® Code Disclaimer:
            </p>
            <p>
              CPT® is a registered trademark of the American Medical Association (AMA). All CPT code educational content,
              procedure summaries, and code descriptions on {brand.name} are independently authored by our team to help
              consumers understand hospital billing codes. We do not reproduce, use, or rely on AMA copyrighted text,
              official CPT code descriptors, or proprietary AMA materials.
            </p>
            <p>
              Our educational summaries are created from publicly available information and are designed solely to help
              consumers navigate healthcare pricing transparency. Any references to CPT codes are for informational
              purposes only to assist users in identifying procedures in hospital price transparency files.
            </p>
            <p className="font-semibold text-foreground mt-4">
              HCPCS Code Disclaimer:
            </p>
            <p>
              HCPCS Level II codes are maintained by the Centers for Medicare & Medicaid Services (CMS). HCPCS Level I
              codes are CPT® codes owned by the AMA. All HCPCS code descriptions and educational content on {brand.name}
              are independently authored and do not reproduce official code descriptors or copyrighted materials.
            </p>
            <p className="font-semibold text-foreground mt-4">
              Other Medical Coding Systems:
            </p>
            <p>
              MS-DRG (Medicare Severity Diagnosis-Related Groups), Revenue Codes, and NDC (National Drug Code) references
              on this platform are used solely for the purpose of analyzing publicly disclosed hospital pricing data. All
              educational content related to these coding systems is independently authored and does not reproduce official
              government or proprietary materials beyond what is necessary for price transparency analysis.
            </p>
            <p className="text-xs italic mt-4">
              {brand.name} respects all intellectual property rights and trademarks. Our use of medical coding references
              is limited to what is necessary to fulfill the public interest purpose of healthcare price transparency under
              CMS regulations.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.dataObfuscation')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p className="font-semibold text-foreground">
              Many hospitals intentionally obfuscate their pricing data through various means, including but not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Publishing data in non-standard, proprietary formats that are difficult to parse or compare</li>
              <li>Using inconsistent medical coding conventions across different procedure types</li>
              <li>Fragmenting pricing across multiple files or data sources</li>
              <li>Omitting critical information such as dosage amounts, unit measurements, or service settings</li>
              <li>Publishing incomplete or outdated transparency files</li>
              <li>Using vague or misleading procedure descriptions</li>
            </ul>
            <p>
              {brand.name} makes a best-effort attempt to normalize this intentionally obfuscated data to enable
              "apples-to-apples" comparisons across hospitals. Our normalization methodology is designed to aggregate
              pricing data in a standardized way that allows consumers to compare similar procedures across different
              healthcare providers.
            </p>
            <p>
              <a href="/data-methodology" className="text-matrix-green hover:underline font-semibold">
                View our complete Data Normalization Methodology →
              </a>
            </p>
            <p className="text-xs italic">
              Our methodology includes detailed explanations of how we build average price indices across CPT/HCPCS, DRG,
              REV, and NDC codes, aggregate dosage variances, and identify pricing outliers.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.dataCalculations')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p className="font-semibold text-foreground">
              IMPORTANT: All pricing analytics, comparisons, rankings, markups, averages, and statistical calculations
              displayed on {brand.name} are ESTIMATES and PROJECTIONS based on our best-effort analysis of publicly
              available hospital data.
            </p>
            <p>
              These calculations may contain errors, omissions, or inaccuracies due to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Variations in how hospitals format and publish their pricing data</li>
              <li>Different methodologies used by hospitals to calculate and report prices</li>
              <li>Data normalization challenges across different medical coding systems</li>
              <li>Incomplete or missing data in hospital transparency files</li>
              <li>Changes in hospital pricing that may not be immediately reflected in our database</li>
              <li>Algorithmic limitations in our data processing and aggregation methods</li>
            </ul>
            <p className="font-semibold text-foreground">
              We strongly encourage all users to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Verify all pricing information directly with the hospital before making any healthcare decisions</li>
              <li>Perform your own calculations and analysis using the raw data available on individual hospital profile pages</li>
              <li>Cross-reference our data with official hospital chargemaster files and transparency reports</li>
              <li>Consult with healthcare providers and insurance companies for accurate, personalized pricing information</li>
              <li>Treat all data on this platform as preliminary research rather than definitive pricing</li>
            </ul>
            <p>
              {brand.name} is NOT responsible for any decisions made based on the data, calculations, or rankings
              displayed on this platform. Users assume all risk and responsibility for verifying information accuracy
              before making healthcare or financial decisions.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.disclaimer')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              The materials on {brand.name} are provided on an 'as is' basis. {brand.name} makes no warranties,
              expressed or implied, and hereby disclaims and negates all other warranties including, without limitation,
              implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement
              of intellectual property or other violation of rights.
            </p>
            <p>
              Further, {brand.name} does not warrant or make any representations concerning the accuracy, likely results,
              or reliability of the use of the materials on its website or otherwise relating to such materials or on any
              sites linked to this site.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.limitationOfLiability')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              In no event shall {brand.name} or its suppliers be liable for any damages (including, without limitation,
              damages for loss of data or profit, or due to business interruption) arising out of the use or inability
              to use the materials on {brand.name}, even if {brand.name} or a {brand.name} authorized representative
              has been notified orally or in writing of the possibility of such damage.
            </p>
            <p className="font-semibold text-foreground">
              This includes, but is not limited to, any claims, demands, or lawsuits from hospitals, healthcare providers,
              or other entities regarding the display, analysis, or comparison of their pricing data.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.userConduct')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              You agree not to use {brand.name} to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Upload, post, or transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable</li>
              <li>Impersonate any person or entity or falsely state or otherwise misrepresent your affiliation with a person or entity</li>
              <li>Upload, post, or transmit any material that contains software viruses or any other computer code designed to interrupt, destroy, or limit functionality</li>
              <li>Interfere with or disrupt the service or servers or networks connected to the service</li>
              <li>Intentionally or unintentionally violate any applicable local, state, national, or international law</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.modifications')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              {brand.name} may revise these terms of service at any time without notice. By using this website,
              you are agreeing to be bound by the then current version of these terms of service.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.governingLaw')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              These terms and conditions are governed by and construed in accordance with the laws of the United States,
              and you irrevocably submit to the exclusive jurisdiction of the courts in that location.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('terms.sections.contactInformation')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="font-mono text-matrix-green">
              {brand.contact.support}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
